import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CAROUSEL_AUTOPLAY_MS = 5200;
const CAROUSEL_TRANSITION_MS = 760;
const CAROUSEL_SWIPE_THRESHOLD = 44;

const normalizeSlideIndex = (slideIndex, slideCount) =>
  ((slideIndex % slideCount) + slideCount) % slideCount;

function HomeCarousel({ slides, ariaLabel }) {
  const carouselSlides = useMemo(() => (Array.isArray(slides) ? slides : []), [slides]);
  const pointerStartRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  const [direction, setDirection] = useState(1);
  const [availableSlideIndexes, setAvailableSlideIndexes] = useState(() => new Set([0]));
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden,
  );

  const isCarouselPaused = isHovered || isFocused || isTouching || !isPageVisible;

  const markSlideAvailable = useCallback((slideIndex) => {
    setAvailableSlideIndexes((currentIndexes) => {
      if (currentIndexes.has(slideIndex)) {
        return currentIndexes;
      }

      const nextIndexes = new Set(currentIndexes);
      nextIndexes.add(slideIndex);
      return nextIndexes;
    });
  }, []);

  const selectSlide = useCallback(
    (requestedIndex, directionHint = 0) => {
      if (carouselSlides.length < 2) {
        return;
      }

      const nextIndex = normalizeSlideIndex(requestedIndex, carouselSlides.length);

      if (nextIndex === index) {
        return;
      }

      markSlideAvailable(nextIndex);
      setPreviousIndex(index);
      setDirection(directionHint || (nextIndex > index ? 1 : -1));
      setIndex(nextIndex);
    },
    [carouselSlides.length, index, markSlideAvailable],
  );

  useEffect(() => {
    if (carouselSlides.length < 2) {
      return undefined;
    }

    let cancelled = false;
    let idleId = 0;
    let timeoutId = 0;

    const preloadSlide = (slideIndex) =>
      new Promise((resolve) => {
        const slideImage = new Image();
        let settled = false;
        const finish = (isAvailable) => {
          if (settled) {
            return;
          }

          settled = true;

          if (!cancelled && isAvailable) {
            markSlideAvailable(slideIndex);
          }

          resolve();
        };

        slideImage.decoding = 'async';
        slideImage.onload = () => finish(true);
        slideImage.onerror = () => finish(false);
        slideImage.src = carouselSlides[slideIndex].src;

        if (slideImage.complete && slideImage.naturalWidth > 0) {
          finish(true);
        } else if (typeof slideImage.decode === 'function') {
          slideImage.decode().then(() => finish(true)).catch(() => {});
        }
      });

    const preloadRemainingSlides = async () => {
      for (let slideIndex = 1; slideIndex < carouselSlides.length; slideIndex += 1) {
        if (cancelled) {
          return;
        }

        await preloadSlide(slideIndex);
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(preloadRemainingSlides, { timeout: 2200 });
    } else {
      timeoutId = window.setTimeout(preloadRemainingSlides, 900);
    }

    return () => {
      cancelled = true;

      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [carouselSlides, markSlideAvailable]);

  useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (previousIndex === null) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => setPreviousIndex(null),
      CAROUSEL_TRANSITION_MS,
    );

    return () => window.clearTimeout(timeoutId);
  }, [index, previousIndex]);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reduceMotion || isCarouselPaused || carouselSlides.length < 2) {
      return undefined;
    }

    const nextIndex = (index + 1) % carouselSlides.length;

    if (!availableSlideIndexes.has(nextIndex)) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => selectSlide(nextIndex, 1),
      CAROUSEL_AUTOPLAY_MS,
    );

    return () => window.clearTimeout(timeoutId);
  }, [availableSlideIndexes, carouselSlides.length, index, isCarouselPaused, selectSlide]);

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') {
      return;
    }

    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    setIsTouching(true);
  };

  const handlePointerUp = (event) => {
    const pointerStart = pointerStartRef.current;
    pointerStartRef.current = null;
    setIsTouching(false);

    if (!pointerStart || pointerStart.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - pointerStart.x;
    const deltaY = event.clientY - pointerStart.y;

    if (
      Math.abs(deltaX) < CAROUSEL_SWIPE_THRESHOLD ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    if (deltaX < 0) {
      selectSlide(index + 1, 1);
    } else {
      selectSlide(index - 1, -1);
    }
  };

  const handlePointerCancel = () => {
    pointerStartRef.current = null;
    setIsTouching(false);
  };

  const handleBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocused(false);
    }
  };

  if (carouselSlides.length === 0) {
    return null;
  }

  return (
    <div
      className={`home-carousel ${direction > 0 ? 'is-forward' : 'is-backward'}`}
      role="region"
      aria-roledescription="carrossel"
      aria-label={ariaLabel || 'Carrossel de fotos'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={handleBlurCapture}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {carouselSlides.map((slide, slideIndex) =>
        availableSlideIndexes.has(slideIndex) ||
        slideIndex === index ||
        slideIndex === previousIndex ? (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            className={`home-carousel-slide ${
              slideIndex === index ? 'is-active' : ''
            } ${slideIndex === previousIndex ? 'is-exiting' : ''}`}
            loading={slideIndex === 0 ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
            aria-hidden={slideIndex === index ? undefined : 'true'}
          />
        ) : null,
      )}

      {carouselSlides.length > 1 ? (
        <div
          className="home-carousel-dots"
          role="group"
          aria-label="Navegação do carrossel"
        >
          {carouselSlides.map((_, slideIndex) => (
            <button
              key={slideIndex}
              type="button"
              className={`home-dot ${slideIndex === index ? 'is-active' : ''}`}
              onClick={() => selectSlide(slideIndex)}
              aria-label={`Ir para foto ${slideIndex + 1}`}
              aria-current={slideIndex === index ? 'true' : 'false'}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default HomeCarousel;
